import { create } from 'zustand';

import { IInfoModalContent, EMeetingType } from '@src/types';

interface IRoomStore {
  isJoinedRoom: boolean;
  setIsJoinedRoom: (joined: boolean) => void;

  isTriggerJoinRoom: boolean;
  setIsTriggerJoinRoom: (join: boolean) => void;

  peerName: string;
  setPeerName: (name: string) => void;

  roomId: string;
  setRoomId: (id: string) => void;

  roomTitle: string;
  setRoomTitle: (title: string) => void;

  isRoomSettingsOpen: boolean;
  setIsRoomSettingsOpen: (isOpen: boolean) => void;

  isChangeLayoutOpen: boolean;
  setIsChangeLayoutOpen: (isOpen: boolean) => void;

  isViewLayoutOpen: boolean;
  setIsViewLayoutOpen: (isOpen: boolean) => void;

  isInviteToMagicPenModal: boolean;
  setInviteToMagicPenModal: (isOpen: boolean) => void;

  isMagicPenVisible: boolean;
  setIsMagicPenVisible: (isVisible: boolean) => void;

  isMagicPenAvailable: boolean;
  setIsMagicPenAvailable: (isAvailable: boolean) => void;

  // Explicit Magic Pen annotate permission granted via MAGIC_PEN_ALLOW_ANNOTATE
  hasMagicPenAnnotatePermission: boolean;
  setHasMagicPenAnnotatePermission: (value: boolean) => void;

  isSessionTimedOut: boolean;
  setIsSessionTimedOut: (value: boolean) => void;

  infoModalContent: IInfoModalContent | null;
  setInfoModalContent: (
    content: IInfoModalContent | null | ((prev: IInfoModalContent | null) => IInfoModalContent | null)
  ) => void;

  isInfoModalOpen: boolean;
  setIsInfoModalOpen: (isOpen: boolean) => void;

  //Array for ask-to-share-screen requests, each object contains the peer id who requested the share, and his request id
  requestInfo: { peerId: string; requestId: string }[] | [];

  setRequestInfo: (requestInfo: { peerId: string; requestId: string }) => void;

  removeRequestInfoByPeerId: (peerId: string) => void;

  resetRequestInfo: () => void;

  meetingType: EMeetingType;
  setMeetingType: (type: EMeetingType) => void;

  checkboxState: boolean;
  setCheckboxState: (checked: boolean) => void;

  accessKey: string;
  setAccessKey: (accessKey: string) => void;

  joinFormErrors: Partial<Record<'username', string>>;
  setJoinFormErrors: (errors: Partial<Record<'username', string>>) => void;

  isActingHost: boolean;
  setIsActingHost: (isActingHost: boolean) => void;
}

export const useRoomStore = create<IRoomStore>((set) => ({
  isJoinedRoom: false,
  setIsJoinedRoom: (isJoinedRoom) =>
    set((state) => {
      return { ...state, isJoinedRoom };
    }),

  isTriggerJoinRoom: false,
  setIsTriggerJoinRoom: (isTriggerJoinRoom) => set(() => ({ isTriggerJoinRoom })),

  peerName: '',
  setPeerName: (peerName) => set(() => ({ peerName })),

  roomId: '',
  setRoomId: (roomId) =>
    set((state) => {
      return { ...state, roomId };
    }),

  roomTitle: '',
  setRoomTitle(roomTitle) {
    set((state) => {
      return { ...state, roomTitle };
    });
  },

  isRoomSettingsOpen: false,
  setIsRoomSettingsOpen(isRoomSettingsOpen) {
    set((state) => {
      return { ...state, isRoomSettingsOpen };
    });
  },

  isChangeLayoutOpen: false,
  setIsChangeLayoutOpen(isChangeLayoutOpen) {
    set(() => ({ isChangeLayoutOpen }));
  },

  isViewLayoutOpen: false,
  isInviteToMagicPenModal: false,
  setInviteToMagicPenModal(isInviteToMagicPenModal) {
    set(() => ({ isInviteToMagicPenModal }));
  },

  isMagicPenVisible: true,
  setIsMagicPenVisible(isMagicPenVisible) {
    set(() => ({ isMagicPenVisible }));
  },

  isMagicPenAvailable: false,
  setIsMagicPenAvailable(isMagicPenAvailable) {
    set(() => ({ isMagicPenAvailable }));
  },

  hasMagicPenAnnotatePermission: false,
  setHasMagicPenAnnotatePermission(hasMagicPenAnnotatePermission) {
    set(() => ({ hasMagicPenAnnotatePermission }));
  },

  setIsViewLayoutOpen(isViewLayoutOpen) {
    set(() => ({ isViewLayoutOpen }));
  },

  isSessionTimedOut: false,
  setIsSessionTimedOut: (isSessionTimedOut: boolean) =>
    set((state) => {
      return { ...state, isSessionTimedOut };
    }),

  infoModalContent: {
    title: '',
    description: '',
    errorPriority: 'MEDIUM',
  },
  setInfoModalContent: (
    content: IInfoModalContent | null | ((prev: IInfoModalContent | null) => IInfoModalContent | null)
  ) =>
    set((state) => ({
      ...state,
      infoModalContent: typeof content === 'function' ? content(state.infoModalContent) : content,
    })),

  isInfoModalOpen: false,
  setIsInfoModalOpen: (isInfoModalOpen: boolean) =>
    set((state) => {
      return { ...state, isInfoModalOpen };
    }),

  checkboxState: false,
  setCheckboxState: (checked) => set({ checkboxState: checked }),

  requestInfo: [],

  setRequestInfo: (newRequest) => {
    set((state) => {
      //handle duplications
      const updatedRequests = state.requestInfo.filter((r) => r.requestId !== newRequest.requestId);
      return {
        requestInfo: [...updatedRequests, newRequest],
      };
    });
  },

  removeRequestInfoByPeerId: (peerId: string) => {
    set((state) => ({
      requestInfo: state.requestInfo.filter((r) => r.peerId !== peerId),
    }));
  },

  resetRequestInfo: () => {
    set(() => ({ requestInfo: [] }));
  },

  meetingType: EMeetingType.MEETING,
  setMeetingType: (meetingType) =>
    set((state) => {
      return { ...state, meetingType };
    }),

  accessKey: '',
  setAccessKey: (accessKey) => set((state) => ({ ...state, accessKey })),

  joinFormErrors: {},
  setJoinFormErrors: (joinFormErrors) =>
    set((state) => ({ ...state, joinFormErrors: { ...state.joinFormErrors, ...joinFormErrors } })),

  isActingHost: false,
  setIsActingHost: (isActingHost) => set(() => ({ isActingHost })),
}));
